// Pure helpers for section photos, videos and links that the server render
// (src/server/pages.js) and the components share. No Vue, no import.meta,
// no server imports: tests/sectionMedia.test.js runs them under plain node.
// Anything that needs URL signing takes the converter as an argument.

const isObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const filled = (v) => typeof v === "string" && v.trim() !== "";

// A stored photo that has something to show: a Pexels pick (src), an
// uploaded or library attachment, or a url. (A studio draft's displayUrl is
// already copied into url by studioLogic's normaliseSection.)
export function hasImage(image) {
    if (!isObject(image)) return false;
    if (isObject(image.pexels) && isObject(image.pexels.src)) return true;
    if (isObject(image.attachment) && filled(image.attachment.url)) return true;
    return filled(image.url);
}

// A stored video that has something to play.
export function hasVideo(video) {
    if (!isObject(video)) return false;
    if (isObject(video.pexels) && Array.isArray(video.pexels.video_files) && video.pexels.video_files.length > 0) return true;
    return filled(video.link) || filled(video.url);
}

// The URL a section's photo shows once the page data is in hand: a Pexels
// pick's large size, else url (signed by the server for a saved page; the
// draft's displayUrl for a studio draft). A saved displayUrl is a stale
// signature and is never preferred here.
export function shownImageUrl(image) {
    if (!isObject(image)) return "";
    const large = isObject(image.pexels) && isObject(image.pexels.src) ? image.pexels.src.large : "";
    if (filled(large)) return large;
    return typeof image.url === "string" ? image.url : "";
}

// The page's social-share image (og:image / twitter:image), chosen from the
// sections people will actually see: hidden sections (Website Studio) are
// skipped. The first visible hero, split, content or areas section with a
// photo wins (a Pexels video's poster counts for a hero or content section);
// otherwise `fallback` (the logo). `convert` turns a stored image into a URL
// (convertImageUrl bound to the business), so call this before the sections'
// photos are converted.
const SHARE_TYPES = new Set(["hero", "split", "content", "areas"]);
export function shareImage(sections, convert, fallback = "") {
    const list = Array.isArray(sections) ? sections : [];
    for (const section of list) {
        if (!isObject(section) || section.hidden === true || !SHARE_TYPES.has(section.type)) continue;
        if (hasImage(section.image)) {
            const url = convert(section.image);
            if (filled(url)) return url;
        }
        const poster = section.type !== "split" && section.type !== "areas" && isObject(section.video?.pexels) ? section.video.pexels.image : "";
        if (filled(poster)) return poster;
    }
    return fallback || "";
}

// ---------------------------------------------------------------- social links

// The business's own social pages for the footer, from businesses.social_media
// ({facebook: {url}, twitter: {url}, instagram, linkedin, youtube, pinterest,
// tiktok}; nullable). Only filled ones, as plain data ({key, label, url}: it
// travels in the page JSON); the footer maps key to its icon. A bare address
// ("facebook.com/me") gets https://; any other scheme is dropped.
export const SOCIAL_NETWORKS = [
    {key: "facebook", label: "Facebook"},
    {key: "instagram", label: "Instagram"},
    {key: "twitter", label: "X"},
    {key: "linkedin", label: "LinkedIn"},
    {key: "youtube", label: "YouTube"},
    {key: "tiktok", label: "TikTok"},
    {key: "pinterest", label: "Pinterest"},
];

export function safeSocialUrl(value) {
    if (typeof value !== "string") return "";
    let v = value.trim();
    if (!v || /\s/.test(v)) return "";
    if (v.startsWith("//")) v = `https:${v}`;
    else if (!/^https?:\/\//i.test(v)) {
        if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return "";
        v = `https://${v}`;
    }
    try {
        const u = new URL(v);
        if (u.protocol !== "https:" && u.protocol !== "http:") return "";
        if (!u.hostname.includes(".") || u.username || u.password) return "";
        return u.href;
    } catch {
        return "";
    }
}

export function socialLinksFrom(socialMedia) {
    if (!isObject(socialMedia)) return [];
    const out = [];
    for (const {key, label} of SOCIAL_NETWORKS) {
        const entry = socialMedia[key];
        const url = safeSocialUrl(isObject(entry) ? entry.url : entry);
        if (url) out.push({key, label, url});
    }
    return out;
}

// ---------------------------------------------------------------- gallery

// Gallery (image-grid) captions show when the section turns them on. The
// studio's editor saves `captions: true`; `showCaptions: true` is accepted too.
export function galleryCaptionsOn(section) {
    return isObject(section) && (section.showCaptions === true || section.captions === true);
}

// One gallery item's caption ('' for none), one line of plain text.
export function captionText(item) {
    if (!isObject(item) || typeof item.caption !== "string") return "";
    return item.caption.replace(/\s+/g, " ").trim().slice(0, 300);
}

// ---------------------------------------------------------------- filmstrip

// One logo/certificate in a filmstrip, as the URL the strip shows ('' when
// it can't show one). Items are attachments; `type` ('image' | 'video' |
// 'pexels' | 'file') may be missing (a photo-library row), so it's worked
// out: a Pexels photo object in `url` is pexels, a video mime type is a
// video, anything else an image. A strip shows pictures only, so videos and
// files give ''. `sign` turns an image attachment into a URL (getImage bound
// to the business).
export function filmstripItemType(item) {
    if (!isObject(item)) return "";
    if (typeof item.type === "string" && item.type) return item.type;
    if (isObject(item.url) && isObject(item.url.src)) return "pexels";
    const mime = typeof item.mime_type === "string" ? item.mime_type : "";
    if (mime.startsWith("video")) return "video";
    return "image";
}

export function filmstripItemUrl(item, sign) {
    if (typeof item === "string") return item;
    if (!isObject(item)) return "";
    switch (filmstripItemType(item)) {
        case "pexels": {
            const large = isObject(item.url) && isObject(item.url.src) ? item.url.src.large : "";
            return filled(large) ? large : "";
        }
        case "image": {
            const hasSource = (isObject(item.attachment) && filled(item.attachment.url)) || filled(item.url);
            if (!hasSource) return "";
            const url = sign(item);
            return filled(url) ? url : "";
        }
        default:
            return "";
    }
}
